import { divIcon, icon } from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { Marker, Popup, Tooltip } from 'components/Map';
import { StyledText } from 'components/Map/Map.style';
import { MARKER_BLUE } from 'utils/constants';

import { getTurbineColor } from '../helpers';

import { windTurbineIcon } from './windTurbineIcon';

const iconDefaults = {
  iconSize: [30, 30],
  shadowSize: [50, 64],
  iconAnchor: [15, 30],
  shadowAnchor: [4, 62],
  popupAnchor: [-3, -76],
};

const createMarkerIcon = (iconUrl, iconProps, selected) =>
  icon({ iconUrl, ...iconDefaults, ...iconProps, className: selected ? 'active' : '' });

const createTurbineSvgIcon = (html, iconProps = {}, selected) =>
  divIcon({
    html,
    className: selected ? 'active turbine-svg-icon' : 'turbine-svg-icon',
    ...iconDefaults,
    popupAnchor: [0, -30],
    ...iconProps,
  });

// TODO: TS-ify this
export function ClickMarker(props) {
  const {
    mapRef,
    content,
    label = '',
    icon,
    iconProps,
    isIconTransparent,
    selected,
    openPopup = false,
    setSelectedTurbine,
    index,
    position,
    isDraggable,
    setHasDraggingStarted,
    // TODO: Make this optional since it's not used eg. in the site details map view
    setTurbineToUpdate,
    name,
    rowIndex,
    assetTypeName,
    inspectionData,
    isWorkOrderTasksList,
    item,
  } = props;
  const theme = useTheme();
  const { maxSeverity } = inspectionData ?? {};

  // isDraggable is true only when preview mode true
  const markerIcon = isDraggable
    ? createMarkerIcon(icon || MARKER_BLUE, iconProps, selected)
    : isWorkOrderTasksList
      ? createTurbineSvgIcon(
          windTurbineIcon(
            item.isLinkedWithTask ? theme.teal : theme.mapPicturePinStroke,
            item.isTasksCompleted,
            theme.green
          ),
          iconProps,
          selected
        )
      : createTurbineSvgIcon(
          windTurbineIcon(getTurbineColor(theme, maxSeverity)),
          iconProps,
          selected
        );

  const [popupRefReady, setPopupRefReady] = useState(false);
  const [markerRefReady, setMarkerRefReady] = useState(false);
  let popupRef = useRef();

  // if the popups ref and markers ref are in the DOM, then allow us to open
  // the popup on the map.
  useEffect(() => {
    if (popupRefReady && markerRefReady && openPopup) {
      popupRef.openOn(mapRef);
    }
  }, [openPopup, popupRefReady, markerRefReady, mapRef]);

  const { lat, lng } = position;

  const closeTimeout = useRef();
  const markerRef = useRef();

  const handleMouseOver = e => {
    if (mapRef?.current) {
      mapRef.current.closePopup();
    }

    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    e.target.openPopup();
  };

  // Some delay, so that user can interact with pop up content
  const handleMouseOut = e => {
    closeTimeout.current = setTimeout(() => {
      e.target.closePopup();
    }, 200);
  };

  useEffect(() => {
    if (isDraggable) {
      return;
    }

    const marker = markerRef.current;
    if (!marker) {
      return;
    }

    let popupDom = null;

    const handleMouseEnter = () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
        closeTimeout.current = null;
      }
    };

    const handleMouseLeave = () => {
      marker.closePopup();
    };

    const handlePopupOpen = () => {
      popupDom = marker.getPopup()?.getElement();
      if (!popupDom) {
        return;
      }

      popupDom.addEventListener('mouseenter', handleMouseEnter);
      popupDom.addEventListener('mouseleave', handleMouseLeave);
    };

    const handlePopupClose = () => {
      if (popupDom) {
        popupDom.removeEventListener('mouseenter', handleMouseEnter);
        popupDom.removeEventListener('mouseleave', handleMouseLeave);
        popupDom = null;
      }
    };

    marker.on('popupopen', handlePopupOpen);
    marker.on('popupclose', handlePopupClose);

    return () => {
      marker.off('popupopen', handlePopupOpen);
      marker.off('popupclose', handlePopupClose);

      if (popupDom) {
        popupDom.removeEventListener('mouseenter', handleMouseEnter);
        popupDom.removeEventListener('mouseleave', handleMouseLeave);
      }

      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
    };
  }, [isDraggable]);

  return (
    <Marker
      position={[lat, lng]}
      icon={markerIcon}
      opacity={isIconTransparent ? 0 : 1}
      draggable={isDraggable}
      ref={_r => {
        if (!isDraggable) {
          markerRef.current = _r;
        }
        setMarkerRefReady(true);
      }}
      eventHandlers={{
        click: () => {
          // When pin is selected from map, also select it in the list
          setSelectedTurbine(index);
        },
        dragstart: () => {
          // before setting new values, need to set selected turbine
          setSelectedTurbine(index);
          setHasDraggingStarted(true);
        },
        dragend: e => {
          setTurbineToUpdate({
            name: name,
            lat: e.target._latlng.lat,
            lng: e.target._latlng.lng,
            rowIndex,
            assetTypeName,
          });
        },
        ...(isDraggable ? {} : { mouseover: handleMouseOver, mouseout: handleMouseOut }),
      }}
    >
      {label && (
        <Tooltip permanent interactive direction="right" className="custom-tooltip">
          <StyledText>{label}</StyledText>
        </Tooltip>
      )}
      {content && (
        <Popup
          ref={r => {
            popupRef = r;
            setPopupRefReady(true);
          }}
        >
          {content}
        </Popup>
      )}
    </Marker>
  );
}
